import React, { useEffect, useState } from "react"
import * as yup from "yup"
import { Box, Typography } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { validateField } from "src/modules/validation"
import InputTextField from "src/components/atom/InputTextField"
import FileSelectField from "src/components/atom/FileSelectField"
import { PublishPanelItem } from "./PublishPanelItem"
import {
  EVENT_STATUS,
  EVENT_TYPE,
  RESPONSE_CODE,
  SNACKBAR_TYPES,
  SORT_ORDER,
} from "src/modules/const"
import dayjs, { Dayjs } from "dayjs"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"
import { useLoginContext } from "src/hooks/useLoginContext"
import InputDateTimeField from "src/components/atom/InputDateTimeField"
import InputField from "src/components/atom/InputField"
import SelectField from "src/components/atom/SelectField"
import { Adviser, Event, EventService, ProfileService } from "src/openapi"
import { SelectDataType } from "src/components/atom/SelectDataType"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { useNavigate } from "react-router-dom"
import { ShowProgress } from "src/states/showProgress"

type Props = {
  item: Event
  closeDetail: any
}

export const EventEditPage = ({ item, closeDetail }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.event" })
  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  const { accessToken, areas, getAreaDetail, checkLoginStatus } = useLoginContext()

  const navigate = useNavigate()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [image, setImage] = useState<string>(item.imageUrl as string)
  const [imageValidationMessage, setImageValidationMessage] = useState<string>("")

  const [title, setTitle] = useState<string>(item.title as string)
  const [description, setDescription] = useState<string>(item.description as string)
  const [areaID, setAreaID] = useState<number>(item.areaId as number)
  const [method, setMethod] = useState<string>(item.type as string)
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs(item.startTime as string))
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs(item.finishTime as string))
  const [joinEndDate, setJoinEndDate] = useState<Dayjs | null>(
    dayjs(item.applicationDeadline as string)
  )
  const [total, setTotal] = useState<string>(
    item.applicationMax ? item.applicationMax.toString() : "0"
  )
  const [ownAdviser, setOwnAdviser] = useState<boolean>(item.adviserId !== 0)
  const [applicationEnable, setApplicationEnable] = useState<boolean>(
    item.isApplicationEnable as boolean
  )
  const [adviserID, setAdviserID] = useState<number>(item.adviserId as number)
  const [contact, setContact] = useState<string>(item.contactInfo as string)

  const [isPublic, setIsPublic] = useState<boolean>(item.isArchivePublic as boolean)
  const [archiveUrl, setArchiveUrl] = useState<string>(item.archiveVideoUrl as string)

  const [titleValidationMessage, setTitleValidationMessage] = useState<string>("")
  const [descriptionValidationMessage, setDescriptionValidationMessage] = useState<string>("")
  const [areaValidationMessage, setAreaValidationMessage] = useState<string>("")
  const [methodValidationMessage, setMethodValidationMessage] = useState<string>("")
  const [startDateValidationMessage, setStartDateValidationMessage] = useState<string>("")
  const [endDateValidationMessage, setEndDateValidationMessage] = useState<string>("")
  const [joinEndDateValidationMessage, setJoinEndDateValidationMessage] = useState<string>("")
  const [totalValidationMessage, setTotalValidationMessage] = useState<string>("")
  const [ownAdviserValidationMessage, setOwnAdviserValidationMessage] = useState<string>("")
  const [contactValidationMessage, setContactValidationMessage] = useState<string>("")
  const [applicationEnableValidationMessage, setApplicationEnableValidationMessage] =
    useState<string>("")

  const [publicValidationMessage, setPublicValidationMessage] = useState<string>("")
  const [archiveUrlValidationMessage, setArchiveValidationMessage] = useState<string>("")

  const [adviser, setAdviser] = useState<SelectDataType[]>([])

  const [category, setCategory] = useState<number>(item.categoryId as number)

  const validate = async () => {
    let ret = true

    if (image) {
      setImageValidationMessage("")
    } else {
      setImageValidationMessage(t("please_select_file"))
      ret = ret && false
    }

    try {
      await validateField(
        yup.object().shape({
          title: yup.string().required(t("please_input_titile")),
        }),
        "title",
        title
      )
      setTitleValidationMessage("")
    } catch (err: any) {
      setTitleValidationMessage(err.message)
      ret = ret && false
    }

    try {
      await validateField(
        yup.object().shape({
          description: yup.string().required(t("please_input_description")),
        }),
        "description",
        description
      )
      setDescriptionValidationMessage("")
    } catch (err: any) {
      setDescriptionValidationMessage(err.message)
      ret = ret && false
    }

    if (areaID === undefined) {
      setAreaValidationMessage(t("please_select_area"))
      ret = ret && false
    } else {
      setAreaValidationMessage("")
    }

    if (method === undefined) {
      setMethodValidationMessage(t("please_select_method"))
      ret = ret && false
    } else {
      setMethodValidationMessage("")
    }

    if (!startDate) {
      setStartDateValidationMessage(t("please_input_date_time"))
      ret = ret && false
    } else if (startDate.isValid()) {
      setStartDateValidationMessage("")
    } else {
      setStartDateValidationMessage(t("please_input_correct_date_time"))
      ret = ret && false
    }

    if (!endDate) {
      setEndDateValidationMessage(t("please_input_date_time"))
      ret = ret && false
    } else if (endDate.isValid()) {
      setEndDateValidationMessage("")
    } else {
      setEndDateValidationMessage(t("please_input_correct_date_time"))
      ret = ret && false
    }

    if (!joinEndDate) {
      setJoinEndDateValidationMessage(t("please_input_date_time"))
      ret = ret && false
    } else if (joinEndDate.isValid()) {
      setJoinEndDateValidationMessage("")
    } else {
      setJoinEndDateValidationMessage(t("please_input_correct_date_time"))
      ret = ret && false
    }

    try {
      await validateField(
        yup.object().shape({
          total: yup.string().matches(/[0-9.]/, t("please_input_number")),
        }),
        "total",
        total
      )
      setTotalValidationMessage("")
    } catch (err: any) {
      setTotalValidationMessage(err.message)
      ret = ret && false
    }

    if (ownAdviser === undefined) {
      setOwnAdviserValidationMessage(t("please_select_own_adviser"))
      ret = ret && false
    } else {
      setOwnAdviserValidationMessage("")
    }

    if (applicationEnable === undefined) {
      setApplicationEnableValidationMessage(t("please_select_application_enable"))
      ret = ret && false
    } else {
      setApplicationEnableValidationMessage("")
    }

    try {
      await validateField(
        yup.object().shape({
          contact: yup.string().required(t("please_input_contact")),
        }),
        "contact",
        contact
      )
      setContactValidationMessage("")
    } catch (err: any) {
      setContactValidationMessage(err.message)
      ret = ret && false
    }

    if (item.status === EVENT_STATUS.ARCHIVED) {
      if (isPublic === undefined) {
        setPublicValidationMessage(t("please_select_archive_type"))
        ret = ret && false
      } else {
        setPublicValidationMessage("")
      }

      if (!archiveUrl) {
        setArchiveValidationMessage(t("please_input_archive_url"))
        ret = ret && false
      } else {
        setArchiveValidationMessage("")
      }
    }

    return ret
  }

  useEffect(() => {
    ProfileService.listAdviser(
      undefined,
      0,
      100,
      undefined,
      undefined,
      undefined,
      "created_at",
      SORT_ORDER.ASCENDING
    ).then((res) => {
      if (res.status === RESPONSE_CODE.SUCCESS) {
        let advisers = []
        for (const adviser of res.data?.advisers as Adviser[]) {
          advisers.push({
            label: `${adviser.lastName} ${adviser.firstName}`,
            value: adviser.id,
          })
        }
        setAdviser(advisers)
        if (adviserID === undefined && advisers.length > 0) {
          setAdviserID(advisers[0].value as number)
        }
      }
    })
  }, [])

  useEffect(() => {
    if (!areaID) {
      return
    }

    setContact(getAreaDetail(areaID).facilityContactInfo)
  }, [areaID])

  const handleUpdate = async () => {
    if (!(await validate())) {
      return
    }

    if (category === undefined) {
      setShowToast({ show: true, text: t("please_select_category"), type: SNACKBAR_TYPES.WARNING })
      return
    }

    setShowProgress(true)
    EventService.updateEvent(accessToken, item.id as number, {
      imageUrl: image,
      title: title,
      description: description,
      startTime: startDate?.format("YYYY-MM-DD HH:mm:ss") as string,
      finishTime: endDate?.format("YYYY-MM-DD HH:mm:ss") as string,
      applicationDeadline: joinEndDate?.format("YYYY-MM-DD HH:mm:ss") as string,
      areaId: areaID as number,
      type: method,
      applicationMax: parseInt(total) as number,
      contactInfo: contact,
      adviserId: ownAdviser ? adviserID : 0,
      categoryId: category,
      isArchivePublic: isPublic,
      isPublic: item.isPublic,
      isFree: item.isFree,
      archiveVideoUrl: archiveUrl,
      isApplicationEnable: !ownAdviser ? applicationEnable : true,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({
            show: true,
            text: t("success_update_event"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          //navigate("/mypage/event/list")
          //navigate(0)
        } else {
          setShowToast({ show: true, text: t("failed_update_event"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_update_event"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleDraftPublic = async () => {
    setShowProgress(true)
    EventService.updateEvent(accessToken, item.id as number, {
      status: EVENT_STATUS.RECEIVING,
      isArchivePublic: item.isArchivePublic,
      isPublic: item.isPublic,
      isFree: item.isFree,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({
            show: true,
            text: t("success_public_event"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          navigate("/mypage/event/list")
          navigate(0)
        } else {
          setShowToast({ show: true, text: t("failed_public_event"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_public_event"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handlePublic = () => {
    setShowProgress(true)
    EventService.updateEvent(accessToken, item.id as number, {
      isPublic: !item.isPublic,
      isArchivePublic: item.isArchivePublic,
      isFree: item.isFree,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }
        if (item.isPublic) {
          if (res.status === RESPONSE_CODE.SUCCESS) {
            setShowToast({
              show: true,
              text: t("success_private_event"),
              type: SNACKBAR_TYPES.SUCCESS,
            })
            item.isPublic = !item.isPublic
          } else {
            setShowToast({
              show: true,
              text: t("failed_private_event"),
              type: SNACKBAR_TYPES.ERROR,
            })
          }
        } else {
          if (res.status === RESPONSE_CODE.SUCCESS) {
            setShowToast({
              show: true,
              text: t("success_public_event"),
              type: SNACKBAR_TYPES.SUCCESS,
            })
            item.isPublic = !item.isPublic
          } else {
            setShowToast({ show: true, text: t("failed_public_event"), type: SNACKBAR_TYPES.ERROR })
          }
        }
      })
      .catch(() => {
        setShowProgress(false)
        if (item.isPublic) {
          setShowToast({ show: true, text: t("failed_private_event"), type: SNACKBAR_TYPES.ERROR })
        } else {
          setShowToast({ show: true, text: t("failed_public_event"), type: SNACKBAR_TYPES.ERROR })
        }
      })
  }

  const handleArchive = (isPublic: boolean, url: string) => {
    setShowProgress(true)
    EventService.updateEvent(accessToken, item.id as number, {
      status: EVENT_STATUS.ARCHIVED,
      isArchivePublic: isPublic,
      archiveVideoUrl: url,
      isPublic: item.isPublic,
      isFree: item.isFree,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }
        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({
            show: true,
            text: t("success_archive"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          navigate("/mypage/event/list")
          navigate(0)
        } else {
          setShowToast({
            show: true,
            text: t("failed_archive"),
            type: SNACKBAR_TYPES.ERROR,
          })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_archive"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleRemove = () => {
    setShowProgress(true)
    EventService.deleteEvent(accessToken, item.id as number)
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({
            show: true,
            text: t("success_remove_event"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          navigate("/mypage/event/list")
          navigate(0)
        } else {
          setShowToast({ show: true, text: t("failed_remove_event"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_remove_event"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <Box
      sx={{
        ...(isTablet
          ? {}
          : { width: "100%", pl: "20px", pr: "20px", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <Typography.FileFieldDetail
        sx={{
          fontFamily: "Noto Sans JP !important",
          my: "12px",
          ml: "20px",
          ":hover": { opacity: 0.8, cursor: "pointer" },
        }}
        onClick={() => closeDetail()}
      >
        {"＜　"}
        {t("page_back")}
      </Typography.FileFieldDetail>

      <Box display={isTablet ? "flex" : "block"}>
        <Box width="100%" sx={{ p: "20px" }}>
          <FileSelectField
            required
            handleChange={setImage}
            file={image}
            validationMessage={imageValidationMessage}
            labelStyle={{
              fontFamily: "Noto Sans JP !important",
              fontSize: "16px",
              fontWeight: "700",
            }}
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          <InputTextField
            required
            name={t("seminar_title")}
            text={title}
            handleText={setTitle}
            validationMessage={titleValidationMessage}
            sx={{ mt: "10px" }}
            labelStyle={{ fontWeight: "700" }}
            inputStyle={{ width: "100%" }}
            multiline
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          <InputTextField
            required
            name={t("seminar_description")}
            text={description}
            handleText={setDescription}
            validationMessage={descriptionValidationMessage}
            sx={{ mt: "20px" }}
            labelStyle={{ fontWeight: "700" }}
            inputStyle={{ width: "100%" }}
            multiline
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          <CheckBoxGroupField
            radio
            required
            datas={areas}
            selected={areaID}
            handleSelected={setAreaID}
            name={t("area")}
            labelSize={16}
            nameSize={16}
            labelStyle={{ fontWeight: "700", fontFamily: "Noto Sans JP !important" }}
            sx={{ mt: "20px" }}
            validationMessage={areaValidationMessage}
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          <CheckBoxGroupField
            radio
            required
            selected={method}
            datas={[
              {
                label: t("online"),
                value: EVENT_TYPE.ONLINE,
              },
              {
                label: t("offline"),
                value: EVENT_TYPE.OFFLINE,
              },
              {
                label: t("both"),
                value: EVENT_TYPE.BOTH,
              },
            ]}
            handleSelected={setMethod}
            name={t("method")}
            nameSize={16}
            labelSize={16}
            labelStyle={{ fontWeight: "700", fontFamily: "Noto Sans JP !important" }}
            sx={{ mt: "20px" }}
            validationMessage={methodValidationMessage}
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          <InputDateTimeField
            required
            name={t("start_date")}
            datetime={startDate}
            handleDateTime={setStartDate}
            validationMessage={startDateValidationMessage}
            inputFormat="YYYY-MM-DD HH:mm"
            sx={{ mt: "20px" }}
            labelStyle={{
              fontWeight: "700",
              fontSize: "16px",
              fontFamily: "Noto Sans JP !important",
            }}
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          <InputDateTimeField
            required
            name={t("end_date")}
            datetime={endDate}
            handleDateTime={setEndDate}
            validationMessage={endDateValidationMessage}
            inputFormat="YYYY-MM-DD HH:mm"
            sx={{ mt: "30px" }}
            labelStyle={{
              fontWeight: "700",
              fontSize: "16px",
              fontFamily: "Noto Sans JP !important",
            }}
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          <InputDateTimeField
            required
            name={t("join_end_date")}
            datetime={joinEndDate}
            handleDateTime={setJoinEndDate}
            validationMessage={joinEndDateValidationMessage}
            inputFormat="YYYY-MM-DD HH:mm"
            sx={{ mt: "30px" }}
            labelStyle={{
              fontWeight: "700",
              fontSize: "16px",
              fontFamily: "Noto Sans JP !important",
            }}
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          <InputTextField
            required
            name={t("total")}
            text={total}
            handleText={setTotal}
            validationMessage={totalValidationMessage}
            sx={{ mt: "30px" }}
            inputStyle={{ width: "100px" }}
            labelStyle={{ fontWeight: "700" }}
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          <CheckBoxGroupField
            radio
            required
            selected={ownAdviser}
            datas={[
              {
                label: t("own_adviser"),
                value: true,
              },
              {
                label: t("outside_adviser"),
                value: false,
              },
            ]}
            handleSelected={setOwnAdviser}
            name={t("adviser_selection")}
            nameSize={16}
            labelSize={16}
            labelStyle={{ fontWeight: "700", fontFamily: "Noto Sans JP !important" }}
            sx={{ mt: "20px" }}
            validationMessage={ownAdviserValidationMessage}
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          {ownAdviser && (
            <SelectField
              required
              name={t("own_adviser_selection")}
              selected={adviserID as number}
              handleSelected={setAdviserID}
              datas={adviser}
              labelStyle={{ fontWeight: "700", fontFamily: "Noto Sans JP !important" }}
              sx={{ mt: "20px" }}
              disabled={item.status === EVENT_STATUS.ARCHIVED}
            />
          )}
          {!ownAdviser && (
            <CheckBoxGroupField
              radio
              required
              selected={applicationEnable}
              datas={[
                {
                  label: t("visible"),
                  value: true,
                },
                {
                  label: t("invisible"),
                  value: false,
                },
              ]}
              handleSelected={setApplicationEnable}
              name={t("application_enable_mark")}
              nameSize={16}
              labelSize={16}
              labelStyle={{ fontWeight: "700", fontFamily: "Noto Sans JP !important" }}
              sx={{ mt: "20px" }}
              validationMessage={applicationEnableValidationMessage}
            />
          )}
          <InputField
            required
            name={t("contact")}
            labelStyle={{ fontWeight: "700", fontSize: "16px" }}
            sx={{ mt: "20px" }}
          />
          <InputTextField
            name={t("select_login_company")}
            text={contact}
            handleText={setContact}
            inputStyle={{ width: "100%" }}
            validationMessage={contactValidationMessage}
            sx={{ mt: "20px" }}
            multiline
            disabled={item.status === EVENT_STATUS.ARCHIVED}
          />
          {item.status === EVENT_STATUS.ARCHIVED && (
            <>
              <CheckBoxGroupField
                required
                radio
                name={t("archive_type")}
                selected={isPublic}
                handleSelected={setIsPublic}
                datas={[
                  {
                    label: t("archive_type_public"),
                    value: true,
                  },
                  {
                    label: t("archive_type_private"),
                    value: false,
                  },
                ]}
                validationMessage={publicValidationMessage}
              />
              <InputTextField
                required
                name={t("archive_url")}
                text={archiveUrl}
                handleText={setArchiveUrl}
                validationMessage={archiveUrlValidationMessage}
                inputStyle={{ width: "100%" }}
                sx={{ mt: "40px" }}
              />
            </>
          )}
        </Box>
        <Box sx={{ bgcolor: "#FAFAFA", py: isTablet ? "0px" : "20px" }}>
          <Box width={isTablet ? "230px" : "100%"} height="100%">
            <PublishPanelItem
              item={item}
              category={category}
              handleCategory={setCategory}
              handleSave={handleUpdate}
              handlePublic={handlePublic}
              handleRemove={handleRemove}
              handleArchive={handleArchive}
              handleDraftPublic={handleDraftPublic}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
